import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fmtDateIST } from "@/lib/dates";
import { ArrowUpDown, Eye, Pencil, Trash2, ChevronLeft, ChevronRight, Truck } from "lucide-react";
import StatusBadge from "@/components/StatusBadge";
import EmptyState from "@/components/EmptyState";

const PER_PAGE = 10;

export default function OrdersTable({ orders, showClient = true, showCreator = true, canEdit = false, onDelete = null, loading = false, selectable = false, selected = new Set(), onToggleSelect = null, onToggleSelectAll = null, onQuickTransit = null }) {
  const navigate = useNavigate();
  const [sort, setSort] = useState({ key: "created_at", dir: -1 });
  const [page, setPage] = useState(1);

  const columns = useMemo(() => [
    { key: "order_id", label: "Order ID" },
    showClient && { key: "client_name", label: "Client" },
    { key: "bdo_reference_no", label: "BDO / Ref No." },
    { key: "passenger_name", label: "Passenger" },
    { key: "passenger_number", label: "Phone" },
    { key: "number_of_bags", label: "Bags", numeric: true },
    { key: "delivery_location", label: "Location" },
    { key: "delivery_type", label: "Type" },
    { key: "delivery_status", label: "Status" },
    showCreator && { key: "created_by_name", label: "Created By" },
    { key: "created_at", label: "Created", date: true },
    { key: "updated_at", label: "Updated", date: true },
  ].filter(Boolean), [showClient, showCreator]);

  const sorted = useMemo(() => {
    const arr = [...orders];
    arr.sort((a, b) => {
      const va = a[sort.key] ?? "";
      const vb = b[sort.key] ?? "";
      if (typeof va === "number" && typeof vb === "number") return (va - vb) * sort.dir;
      return String(va).localeCompare(String(vb)) * sort.dir;
    });
    return arr;
  }, [orders, sort]);

  const pages = Math.max(1, Math.ceil(sorted.length / PER_PAGE));
  const current = Math.min(page, pages);
  const rows = sorted.slice((current - 1) * PER_PAGE, current * PER_PAGE);
  const pageIds = rows.map((r) => r.id);
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selected.has(id));

  const toggleSort = (key) =>
    setSort((s) => ({ key, dir: s.key === key ? -s.dir : 1 }));

  if (loading) {
    return (
      <div className="p-6 space-y-3" data-testid="orders-table-loading">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="h-10 bg-slate-100 rounded-sm animate-pulse" />
        ))}
      </div>
    );
  }

  if (!orders.length) return <EmptyState title="No orders found" testId="orders-empty" />;

  return (
    <div data-testid="orders-table">
      {/* Mobile cards */}
      <div className="md:hidden divide-y divide-slate-100" data-testid="orders-mobile-list">
        {rows.map((o) => (
          <div key={o.id} className="p-4 space-y-2" data-testid={`order-card-${o.order_id}`}>
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                {selectable && (
                  <input type="checkbox" checked={selected.has(o.id)} onChange={() => onToggleSelect?.(o.id)} data-testid={`select-order-mobile-${o.order_id}`} className="h-4 w-4 accent-[#1B3A6B]" />
                )}
                <button onClick={() => navigate(`/orders/${o.id}`)} className="font-semibold text-primary text-sm">{o.order_id}</button>
              </div>
              <div className="flex items-center gap-2">
                <StatusBadge status={o.delivery_status} />
                {o.is_closed && <span className="px-1.5 py-0.5 rounded-sm text-[10px] font-semibold bg-slate-700 text-white">Closed</span>}
              </div>
            </div>
            <div className="text-sm font-medium text-slate-800">{o.passenger_name} <span className="text-slate-400 font-normal">· {o.number_of_bags} bag{o.number_of_bags > 1 ? "s" : ""}</span></div>
            {showClient && <div className="text-xs text-slate-500">{o.client_name}</div>}
            <div className="text-xs text-slate-500 truncate">{o.delivery_location}</div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-slate-500 tnum">{o.delivery_type} · {fmtDateIST(o.created_at)}</span>
              <div className="flex items-center gap-1">
                {onQuickTransit && o.delivery_status === "Picked Up" && (
                  <button
                    data-testid={`quick-transit-mobile-${o.order_id}`}
                    title="Mark Out for Delivery"
                    onClick={() => onQuickTransit(o)}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-sm text-[11px] font-semibold text-orange-600 bg-orange-50 border border-orange-200 hover:bg-orange-100 transition-colors duration-150"
                  >
                    <Truck className="h-3.5 w-3.5" /> Out for Delivery
                  </button>
                )}
                <button data-testid={`view-order-mobile-${o.order_id}`} onClick={() => navigate(`/orders/${o.id}`)} className="p-1.5 rounded-sm text-slate-500 hover:text-primary hover:bg-primary/10 transition-colors duration-150"><Eye className="h-4 w-4" /></button>
                {canEdit && <button data-testid={`edit-order-mobile-${o.order_id}`} onClick={() => navigate(`/orders/${o.id}`, { state: { edit: true } })} className="p-1.5 rounded-sm text-slate-500 hover:text-primary hover:bg-primary/10 transition-colors duration-150"><Pencil className="h-4 w-4" /></button>}
                {onDelete && <button data-testid={`delete-order-mobile-${o.order_id}`} onClick={() => onDelete(o)} className="p-1.5 rounded-sm text-slate-500 hover:text-[#E63946] hover:bg-[#E63946]/10 transition-colors duration-150"><Trash2 className="h-4 w-4" /></button>}
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* Desktop table */}
      <div className="overflow-x-auto hidden md:block">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200 sticky top-0 z-10">
              {selectable && (
                <th className="px-3 py-3 w-8">
                  <input
                    type="checkbox"
                    checked={allPageSelected}
                    onChange={() => onToggleSelectAll?.(pageIds)}
                    data-testid="select-all-orders-page"
                    className="h-4 w-4 accent-[#1B3A6B]"
                  />
                </th>
              )}
              {columns.map((c) => (
                <th
                  key={c.key}
                  onClick={() => toggleSort(c.key)}
                  data-testid={`sort-${c.key}`}
                  className={`px-3 py-3 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500 cursor-pointer select-none whitespace-nowrap hover:text-slate-800 transition-colors duration-150 ${c.numeric ? "text-right" : "text-left"}`}
                >
                  <span className="inline-flex items-center gap-1">
                    {c.label}
                    <ArrowUpDown className={`h-3 w-3 ${sort.key === c.key ? "text-primary" : "text-slate-300"}`} />
                  </span>
                </th>
              ))}
              <th className="px-3 py-3 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((o) => (
              <tr key={o.id} className="border-b border-slate-100 even:bg-slate-50/60 hover:bg-blue-50/40 transition-colors duration-100" data-testid={`order-row-${o.order_id}`}>
                {selectable && (
                  <td className="px-3 py-2.5 w-8">
                    <input type="checkbox" checked={selected.has(o.id)} onChange={() => onToggleSelect?.(o.id)} data-testid={`select-order-${o.order_id}`} className="h-4 w-4 accent-[#1B3A6B]" />
                  </td>
                )}
                <td className="px-3 py-2.5 font-semibold text-primary whitespace-nowrap">{o.order_id}</td>
                {showClient && <td className="px-3 py-2.5 text-slate-700 whitespace-nowrap max-w-[160px] truncate">{o.client_name}</td>}
                <td className="px-3 py-2.5 text-slate-600 whitespace-nowrap">{o.bdo_reference_no || "—"}</td>
                <td className="px-3 py-2.5 text-slate-800 font-medium whitespace-nowrap">{o.passenger_name}</td>
                <td className="px-3 py-2.5 text-slate-600 whitespace-nowrap tnum">{o.passenger_number}</td>
                <td className="px-3 py-2.5 text-right text-slate-800 tnum">{o.number_of_bags}</td>
                <td className="px-3 py-2.5 text-slate-600 max-w-[180px] truncate" title={o.delivery_location}>{o.delivery_location}</td>
                <td className="px-3 py-2.5 whitespace-nowrap">
                  <span className={`text-xs font-medium ${o.delivery_type === "Express" ? "text-[#E63946]" : "text-slate-600"}`}>{o.delivery_type}</span>
                </td>
                <td className="px-3 py-2.5 text-center whitespace-nowrap">
                  <StatusBadge status={o.delivery_status} />
                  {o.is_closed && <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded-sm text-[10px] font-semibold bg-slate-700 text-white">Closed</span>}
                </td>
                {showCreator && <td className="px-3 py-2.5 text-slate-600 whitespace-nowrap">{o.created_by_name}</td>}
                <td className="px-3 py-2.5 text-slate-500 whitespace-nowrap text-xs tnum">{fmtDateIST(o.created_at)}</td>
                <td className="px-3 py-2.5 text-slate-500 whitespace-nowrap text-xs tnum">{fmtDateIST(o.updated_at)}</td>
                <td className="px-3 py-2.5">
                  <div className="flex items-center justify-end gap-1">
                    {onQuickTransit && o.delivery_status === "Picked Up" && (
                      <button
                        data-testid={`quick-transit-${o.order_id}`}
                        title="Mark Out for Delivery"
                        onClick={() => onQuickTransit(o)}
                        className="p-1.5 rounded-sm text-slate-500 hover:text-orange-600 hover:bg-orange-50 transition-colors duration-150"
                      >
                        <Truck className="h-4 w-4" />
                      </button>
                    )}
                    <button
                      data-testid={`view-order-${o.order_id}`}
                      title="View details"
                      onClick={() => navigate(`/orders/${o.id}`)}
                      className="p-1.5 rounded-sm text-slate-500 hover:text-primary hover:bg-primary/10 transition-colors duration-150"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                    {canEdit && (
                      <button
                        data-testid={`edit-order-${o.order_id}`}
                        title="Edit order"
                        onClick={() => navigate(`/orders/${o.id}`, { state: { edit: true } })}
                        className="p-1.5 rounded-sm text-slate-500 hover:text-primary hover:bg-primary/10 transition-colors duration-150"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                    )}
                    {onDelete && (
                      <button
                        data-testid={`delete-order-${o.order_id}`}
                        title="Delete order"
                        onClick={() => onDelete(o)}
                        className="p-1.5 rounded-sm text-slate-500 hover:text-[#E63946] hover:bg-[#E63946]/10 transition-colors duration-150"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200" data-testid="orders-pagination">
        <span className="text-xs text-slate-500 tnum">
          Showing {(current - 1) * PER_PAGE + 1}–{Math.min(current * PER_PAGE, sorted.length)} of {sorted.length} orders
        </span>
        <div className="flex items-center gap-1">
          <button
            data-testid="pagination-prev"
            disabled={current <= 1}
            onClick={() => setPage(current - 1)}
            className="p-1.5 rounded-sm border border-slate-200 text-slate-600 disabled:opacity-40 hover:bg-slate-50 transition-colors duration-150"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="text-xs text-slate-600 px-2 tnum" data-testid="pagination-info">Page {current} of {pages}</span>
          <button
            data-testid="pagination-next"
            disabled={current >= pages}
            onClick={() => setPage(current + 1)}
            className="p-1.5 rounded-sm border border-slate-200 text-slate-600 disabled:opacity-40 hover:bg-slate-50 transition-colors duration-150"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
